import { Injectable } from '@angular/core';
import { ReadonlyIndexedDbDictionaryRepository } from './readonly-indexed-db-dictionary.repository';
import { DictionaryRepository } from '../abstraction';
import { Dictionary, IndexedDbDataAccess } from '@softline/core';

@Injectable()
export abstract class IndexedDbDictionaryRepository<
    TItem,
    TParams extends Dictionary<any> | undefined,
  >
  extends ReadonlyIndexedDbDictionaryRepository<TItem, TParams>
  implements DictionaryRepository<TItem, Dictionary<TItem>, TItem, TParams, TParams>
{
  protected constructor(
    dataAccess: IndexedDbDataAccess,
    databaseName: string,
    objectStoreName: string
  ) {
    super(dataAccess, databaseName, objectStoreName);
  }

  async save(dictionary: Dictionary<TItem>, params: TParams): Promise<Dictionary<TItem>> {
    await this.dataAccess.clear({
      databaseName: this.databaseName,
      objectStoreName: this.objectStoreName,
      params,
    });
    for (const [key, value] of Object.entries(dictionary)) {
      await this.dataAccess.add(
        {
          databaseName: this.databaseName,
          objectStoreName: this.objectStoreName,
          key,
          params,
        },
        value
      );
    }
    return dictionary;
  }

  async saveItem(key: string, value: TItem, params: TParams): Promise<TItem> {
    return await this.dataAccess.add(
      {
        databaseName: this.databaseName,
        objectStoreName: this.objectStoreName,
        key,
        params,
      },
      value
    );
  }

  async updateItem(key: string, value: TItem, params: TParams): Promise<TItem> {
    return await this.dataAccess.put(
      {
        databaseName: this.databaseName,
        objectStoreName: this.objectStoreName,
        key,
        params,
      },
      value
    );
  }

  async deleteItem(key: string, params: TParams): Promise<TItem> {
    return await this.dataAccess.delete({
      databaseName: this.databaseName,
      objectStoreName: this.objectStoreName,
      key,
      params,
    });
  }

  async delete(params: TParams): Promise<Dictionary<TItem>> {
    const response = await this.dataAccess.clear<TItem>({
      databaseName: this.databaseName,
      objectStoreName: this.objectStoreName,
      params,
    });
    return this.mapResponse(response);
  }
}
